// src/components/tfmodules/tfModulesPage.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Page,
  Header,
  Content,
  Progress,
  Table,
  InfoCard,
} from '@backstage/core-components';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  Tooltip,
  IconButton,
  Link as MuiLink,
  makeStyles,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

/** Envs shown as columns, matching DISPLAY_ENVS in the backend helper. */
const ENVS = ['dev', 'qa', 'uat', 'prd'] as const;
type Env = (typeof ENVS)[number];

type EnvTags = Partial<Record<Env | 'sbx', string>>;

type GroupPin = {
  groupName: string;
  groupId?: number;
  found: boolean;
  tags: EnvTags;
};

type TfModuleStatus = 'current' | 'outdated' | 'unknown';

/** One env where a project's own deploy pin disagrees with the shared tf_plan pin. */
type EnvDisagreement = { env: Env; deploy: string; prCheck: string };

/**
 * The ADO-project-wide tf_plan pin. Reported once per ADO project, not per Backstage
 * project: the tf_plan check is registered with no repositoryId, so it covers every repo
 * in the ADO project and is shared by all Backstage projects under it.
 */
type AdoProjectNotice = {
  adoProject: string;
  groupName: string;
  tags: EnvTags;
  libraryUrl: string;
  disagreements: Array<{ project: string; envs: EnvDisagreement[] }>;
};

type TfModuleRow = {
  name: string;
  adoProject: string;
  adoProjectDisplay: string;
  repos: number;
  deploy: GroupPin;
  prCheck: GroupPin;
  status: TfModuleStatus;
  libraryUrl: string;
};

type TfModulePayload = {
  latest?: string;
  latestUrl?: string;
  projects: TfModuleRow[];
  notices?: AdoProjectNotice[];
  warnings: string[];
  generatedAt: string;
  fromCache?: boolean;
};

// Same red/green pair the cost tables already use (projectDetails.tsx:616-630), so the
// page reads as part of the existing UI rather than introducing a second palette.
const BEHIND = '#d32f2f';
const OK = '#388e3c';

const useStyles = makeStyles(theme => ({
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filter: {
    minWidth: 280,
  },
  meta: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  caption: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  chipCurrent: {
    backgroundColor: OK,
    color: theme.palette.common.white,
  },
  chipOutdated: {
    backgroundColor: BEHIND,
    color: theme.palette.common.white,
  },
  warnings: {
    marginBottom: theme.spacing(2),
  },
  noticeBody: {
    color: theme.palette.text.secondary,
  },
}));

/** The pin actually in effect: the deploy group, or the tf_plan group as a fallback. */
function effectivePin(row: TfModuleRow): GroupPin {
  if (row.deploy.found) return row.deploy;
  if (row.prCheck.found) return row.prCheck;
  return row.deploy;
}

export const TfModulesPage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [data, setData] = useState<TfModulePayload>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [filter, setFilter] = useState('');
  const [outdatedOnly, setOutdatedOnly] = useState(false);

  const fetchVersions = useCallback(
    async (force = false) => {
      setLoading(true);
      setError(undefined);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        const res = await fetch(`${baseUrl}/tf-modules/versions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ forceRefresh: force }),
        });
        if (!res.ok) {
          throw new Error(
            `Failed to load terraform module versions: ${res.status}`,
          );
        }
        setData((await res.json()) as TfModulePayload);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, identityApi],
  );

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const rows = useMemo(() => {
    const all = data?.projects ?? [];
    const needle = filter.trim().toLowerCase();
    return all.filter(r => {
      if (outdatedOnly && r.status !== 'outdated') {
        return false;
      }
      if (!needle) return true;
      return (
        r.name.toLowerCase().includes(needle) ||
        r.adoProjectDisplay.toLowerCase().includes(needle)
      );
    });
  }, [data, filter, outdatedOnly]);

  const latest = data?.latest;

  const renderTag = (row: TfModuleRow, env: Env) => {
    const pin = effectivePin(row);
    const tag = pin.tags[env];
    if (!tag) {
      return <Typography variant="body2">—</Typography>;
    }
    const behind = !!latest && tag !== latest;
    return (
      <Typography variant="body2" style={{ color: behind ? BEHIND : OK }}>
        {tag}
      </Typography>
    );
  };

  const statusChip = (row: TfModuleRow) => {
    switch (row.status) {
      case 'current':
        return <Chip size="small" label="current" className={classes.chipCurrent} />;
      case 'outdated':
        return <Chip size="small" label="outdated" className={classes.chipOutdated} />;
      default:
        return (
          <Tooltip
            title={
              row.prCheck.found
                ? `'${row.deploy.groupName}' does not exist, so this project has no deploy ` +
                  `pin of its own. The values shown come from the ADO-project-wide ` +
                  `'${row.prCheck.groupName}'.`
                : `No variable group found (looked for '${row.deploy.groupName}' and '${row.prCheck.groupName}')`
            }
          >
            <Chip size="small" label="unknown" variant="outlined" />
          </Tooltip>
        );
    }
  };

  const columns = [
    {
      title: 'Project',
      field: 'name',
      render: (row: TfModuleRow) => (
        <>
          <Typography variant="body2">{row.name}</Typography>
          <Typography variant="caption" className={classes.caption}>
            {row.adoProjectDisplay}
            {!row.deploy.found && row.prCheck.found ? ' · tf_plan pin only' : ''}
          </Typography>
        </>
      ),
    },
    {
      title: 'Repos',
      field: 'repos',
      type: 'numeric' as const,
      width: '80px',
    },
    ...ENVS.map(env => ({
      title: env,
      field: `deploy.tags.${env}`,
      width: '90px',
      render: (row: TfModuleRow) => renderTag(row, env),
    })),
    {
      title: 'Latest',
      field: 'latest',
      width: '110px',
      render: () =>
        latest && data?.latestUrl ? (
          <MuiLink href={data.latestUrl} target="_blank" rel="noopener noreferrer">
            {latest}
          </MuiLink>
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      title: 'Status',
      field: 'status',
      width: '120px',
      render: (row: TfModuleRow) => statusChip(row),
    },
    {
      title: 'Library',
      field: 'libraryUrl',
      width: '80px',
      render: (row: TfModuleRow) => (
        <Tooltip title={`Open ${effectivePin(row).groupName} in Azure DevOps`}>
          <IconButton
            size="small"
            component="a"
            href={row.libraryUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${effectivePin(row).groupName} in Azure DevOps`}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Page themeId="home">
      <Header
        title="Terraform Modules"
        subtitle={
          latest ? `Latest terraform-modules tag: ${latest}` : undefined
        }
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => fetchVersions(true)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Header>
      <Content>
        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}

        {loading ? (
          <Progress />
        ) : (
          <>
            {!!data?.notices?.length && (
              <Box className={classes.warnings}>
                <InfoCard
                  title={`tf_plan version conflicts (${data.notices.length} ADO ${
                    data.notices.length === 1 ? 'project' : 'projects'
                  })`}
                  subheader={
                    'The tf_plan PR check is registered project-wide, so it uses one ' +
                    'shared variable group for every repo in the ADO project. Where that ' +
                    'differs from a project’s own deploy pin, PRs are planned against ' +
                    'module code that will not be deployed.'
                  }
                >
                  {data.notices.map(n => (
                    <Box key={n.adoProject} mb={2}>
                      <Typography variant="subtitle2">
                        {n.adoProject}
                        {' — '}
                        <MuiLink
                          href={n.libraryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {n.groupName}
                        </MuiLink>
                      </Typography>
                      {n.disagreements.map(d => (
                        <Typography
                          key={d.project}
                          variant="body2"
                          className={classes.noticeBody}
                        >
                          {'• '}
                          {d.project}:{' '}
                          {d.envs
                            .map(
                              e =>
                                `${e.env} deploys ${e.deploy}, tf_plan uses ${e.prCheck}`,
                            )
                            .join('; ')}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </InfoCard>
              </Box>
            )}

            {!!data?.warnings.length && (
              <Box className={classes.warnings}>
                <InfoCard title={`Warnings (${data.warnings.length})`}>
                  {data.warnings.map(w => (
                    <Typography key={w} variant="body2">
                      • {w}
                    </Typography>
                  ))}
                </InfoCard>
              </Box>
            )}

            <Box className={classes.controls}>
              <TextField
                className={classes.filter}
                label="Filter"
                variant="outlined"
                size="small"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
              <Button
                variant={outdatedOnly ? 'contained' : 'outlined'}
                color={outdatedOnly ? 'primary' : 'default'}
                onClick={() => setOutdatedOnly(v => !v)}
              >
                Show outdated only
              </Button>
              <Typography variant="caption" className={classes.meta}>
                {rows.length} of {data?.projects.length ?? 0} projects
                {data?.fromCache ? ' · cached' : ''}
                {data?.generatedAt
                  ? ` · as of ${new Date(data.generatedAt).toLocaleString()}`
                  : ''}
              </Typography>
            </Box>

            <Table<TfModuleRow>
              options={{ paging: false, search: false, showTitle: false }}
              columns={columns as any}
              data={rows}
            />
          </>
        )}
      </Content>
    </Page>
  );
};
